import { CHARGEBEE_SITE } from "@/utilities/configurations";

declare global {
  interface Window {
    Chargebee: {
      init: (config: { site: string }) => void;
      getInstance: () => any;
      getPortalSections: () => Record<string, string>;
    };
  }
}

let chargebeeScriptPromise: Promise<void> | null = null;
let chargebeeInitialized = false;

/**
 * Loads the Chargebee SDK script dynamically and initializes it.
 * Uses a singleton promise pattern (same as the Crisp loader).
 * Safe to call multiple times — the script is only loaded once.
 */
export const loadAndInitChargebee = (): Promise<void> => {
  if (window.location.href.includes("demo.")) {
    return Promise.resolve();
  }

  if (chargebeeScriptPromise) {
    return chargebeeScriptPromise;
  }

  chargebeeScriptPromise = new Promise<void>((resolve, reject) => {
    if (window.Chargebee && typeof window.Chargebee.init === "function") {
      initChargebee();
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.chargebee.com/v2/chargebee.js";
    script.async = true;
    script.onload = () => {
      initChargebee();
      resolve();
    };
    script.onerror = () => {
      chargebeeScriptPromise = null;
      reject(new Error("Failed to load Chargebee SDK"));
    };
    document.head.appendChild(script);
  });

  return chargebeeScriptPromise;
};

function initChargebee() {
  if (chargebeeInitialized) return;
  if (window.Chargebee && typeof window.Chargebee.init === "function") {
    window.Chargebee.init({ site: CHARGEBEE_SITE });
    chargebeeInitialized = true;
  }
}
